import { useState, useEffect } from 'react';

interface FormData {
  parentName: string;
  parentEmail: string;
  parentPhone: string;
  playerName: string;
  playerAge: string;
  playerGender: string;
  program: string;
  experience: string;
  medicalConditions: string;
  emergencyContact: string;
  emergencyPhone: string;
  marketingConsent: boolean;
  paymentPlan: string;
}

const RegistrationForm = () => {
  const [formData, setFormData] = useState<FormData>({
    parentName: '',
    parentEmail: '',
    parentPhone: '',
    playerName: '',
    playerAge: '',
    playerGender: '',
    program: '',
    experience: '',
    medicalConditions: '',
    emergencyContact: '',
    emergencyPhone: '',
    marketingConsent: false,
    paymentPlan: 'monthly'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentCanceled, setPaymentCanceled] = useState(false);

  // Check for payment status in URL params
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success') === 'true') {
      setPaymentSuccess(true);
    }
    if (urlParams.get('canceled') === 'true') {
      setPaymentCanceled(true);
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const calculateTotalAmount = () => {
    const programPricing = {
      'recreational-u6': { monthly: 100, quarterly: 285, annual: 1080 },
      'recreational-u8': { monthly: 110, quarterly: 313.50, annual: 1188 },
      'recreational-u10': { monthly: 120, quarterly: 342, annual: 1296 },
      'competitive-u12': { monthly: 150, quarterly: 427.50, annual: 1620 },
      'competitive-u14': { monthly: 160, quarterly: 456, annual: 1728 },
      'competitive-u16': { monthly: 180, quarterly: 513, annual: 1944 },
      'elite-u18': { monthly: 200, quarterly: 570, annual: 2160 },
      'summer-camp': { weekly: 300 },
      'winter-training': { monthly: 120, quarterly: 342, annual: 1296 }
    };

    const registrationFee = 80;
    let programAmount = 0;

    if (formData.program === 'summer-camp') {
      programAmount = programPricing[formData.program]?.weekly || 0;
    } else {
      programAmount = programPricing[formData.program as keyof typeof programPricing]?.[formData.paymentPlan as keyof typeof programPricing['recreational-u6']] || 0;
    }

    return programAmount + registrationFee;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      console.log('Submitting registration with data:', formData);
      
      // Create Stripe checkout session using correct Supabase Edge Function URL
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
      
      if (!supabaseUrl || !supabaseKey) {
        throw new Error('Supabase configuration missing. Please contact support.');
      }

      console.log('Calling Edge Function:', `${supabaseUrl}/functions/v1/create-payment-session`);

      const response = await fetch(`${supabaseUrl}/functions/v1/create-payment-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseKey}`,
          'apikey': supabaseKey
        },
        body: JSON.stringify({
          program: formData.program,
          paymentPlan: formData.paymentPlan,
          registrationData: formData
        })
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));
      
      // Get response text first to handle both JSON and non-JSON responses
      const responseText = await response.text();
      console.log('Response text:', responseText);
      
      let responseData;
      try {
        responseData = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Failed to parse response as JSON:', parseError);
        throw new Error(`Server returned invalid response: ${responseText.substring(0, 200)}`);
      }
      
      if (!response.ok) {
        console.error('API Error Response:', responseData);
        const errorMessage = responseData.error || responseData.message || `HTTP ${response.status}: Failed to create payment session`;
        throw new Error(errorMessage);
      }

      const { url, sessionId, success } = responseData;
      console.log('Payment session created:', { url, sessionId, success });
      
      if (!success || !url) {
        throw new Error(responseData.error || 'No checkout URL received from payment service');
      }
      
      // Redirect to Stripe checkout
      console.log('Redirecting to Stripe checkout:', url);
      window.location.href = url;
      
    } catch (error) {
      console.error('Payment session creation error:', error);
      console.error('Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        type: typeof error,
        error: error
      });
      
      setSubmitStatus('error');
      setIsSubmitting(false);
      
      // Show more specific error message
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred. Please try again.';
      alert(`Payment Error: ${errorMessage}\n\nIf this problem persists, please contact support.`);
    }
  };

  const programs = [
    { value: 'recreational-u6', label: 'Recreational (Under 6)', price: '$100/month' },
    { value: 'recreational-u8', label: 'Recreational (Under 8)', price: '$110/month' },
    { value: 'recreational-u10', label: 'Recreational (Under 10)', price: '$120/month' },
    { value: 'competitive-u12', label: 'Competitive (Under 12)', price: '$150/month' },
    { value: 'competitive-u14', label: 'Competitive (Under 14)', price: '$160/month' },
    { value: 'competitive-u16', label: 'Competitive (Under 16)', price: '$180/month' },
    { value: 'elite-u18', label: 'Elite Training (Under 18)', price: '$200/month' },
    { value: 'summer-camp', label: 'Summer Camp', price: '$300/week' },
    { value: 'winter-training', label: 'Winter Training', price: '$120/month' }
  ];

  // Show success message if payment was completed
  if (paymentSuccess) {
    return (
      <div className="bg-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="bg-green-50 border border-green-200 rounded-lg p-8 mb-8">
              <i className="ri-check-circle-line text-green-600 text-6xl mb-4"></i>
              <h2 className="text-3xl font-bold text-green-800 mb-4">Registration Successful!</h2>
              <p className="text-lg text-green-700 mb-6">
                Thank you for registering with Nextzen Soccer Academy. Your payment has been processed successfully.
              </p>
              <p className="text-green-600">
                We'll contact you within 24 hours with program details and next steps.
              </p>
            </div>
            <button
              onClick={() => window.location.href = '/'}
              className="bg-purple-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors whitespace-nowrap"
            >
              Return to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Player Registration</h2>
          <p className="text-lg text-gray-600">Join Elite Soccer Academy and start your soccer journey today!</p>
        </div>

        {/* Payment canceled message */}
        {paymentCanceled && (
          <div className="mb-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-center">
              <i className="ri-information-line text-yellow-600 mr-2"></i>
              <span className="text-yellow-800">Payment was canceled. You can complete your registration below.</span>
            </div>
          </div>
        )}

        <form 
          id="soccer-registration-form"
          data-readdy-form
          onSubmit={handleSubmit} 
          className="bg-gray-50 rounded-lg p-8 shadow-lg"
        >
          {/* Parent/Guardian Information */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <i className="ri-parent-line text-purple-600 mr-2"></i>
              Parent/Guardian Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="parentName" className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="parentName"
                  name="parentName"
                  value={formData.parentName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                  placeholder="Enter your full name"
                />
              </div>
              <div>
                <label htmlFor="parentEmail" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="parentEmail"
                  name="parentEmail"
                  value={formData.parentEmail}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                  placeholder="Enter your email"
                />
              </div>
              <div>
                <label htmlFor="parentPhone" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="parentPhone"
                  name="parentPhone"
                  value={formData.parentPhone}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>
          </div>

          {/* Player Information */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <i className="ri-user-line text-purple-600 mr-2"></i>
              Player Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="playerName" className="block text-sm font-medium text-gray-700 mb-2">
                  Player Full Name *
                </label>
                <input
                  type="text"
                  id="playerName"
                  name="playerName"
                  value={formData.playerName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                  placeholder="Enter player's full name"
                />
              </div>
              <div>
                <label htmlFor="playerAge" className="block text-sm font-medium text-gray-700 mb-2">
                  Player Age *
                </label>
                <select
                  id="playerAge"
                  name="playerAge"
                  value={formData.playerAge}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm pr-8"
                >
                  <option value="">Select age</option>
                  {Array.from({ length: 14 }, (_, i) => i + 4).map(age => (
                    <option key={age} value={age}>{age} years old</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="playerGender" className="block text-sm font-medium text-gray-700 mb-2">
                  Gender *
                </label>
                <select
                  id="playerGender"
                  name="playerGender"
                  value={formData.playerGender}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm pr-8"
                >
                  <option value="">Select gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>
              <div>
                <label htmlFor="experience" className="block text-sm font-medium text-gray-700 mb-2">
                  Soccer Experience *
                </label>
                <select
                  id="experience"
                  name="experience"
                  value={formData.experience}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm pr-8"
                >
                  <option value="">Select experience level</option>
                  <option value="beginner">Beginner (0-1 years)</option>
                  <option value="intermediate">Intermediate (2-4 years)</option>
                  <option value="advanced">Advanced (5+ years)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Program Selection */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <i className="ri-football-line text-purple-600 mr-2"></i>
              Program Selection
            </h3>
            <div>
              <label htmlFor="program" className="block text-sm font-medium text-gray-700 mb-2">
                Choose Program *
              </label>
              <select
                id="program"
                name="program"
                value={formData.program}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm pr-8"
              >
                <option value="">Select a program</option>
                {programs.map(program => (
                  <option key={program.value} value={program.value}>
                    {program.label} - {program.price}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Payment Plan */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <i className="ri-bank-card-line text-purple-600 mr-2"></i>
              Payment Plan
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <label className="flex items-center p-4 border border-gray-300 rounded-lg cursor-pointer hover:bg-purple-50">
                <input
                  type="radio"
                  name="paymentPlan"
                  value="monthly"
                  checked={formData.paymentPlan === 'monthly'}
                  onChange={handleInputChange}
                  className="mr-3 text-purple-600"
                />
                <div>
                  <div className="font-medium text-gray-900">Monthly</div>
                  <div className="text-sm text-gray-600">Pay monthly</div>
                </div>
              </label>
              <label className="flex items-center p-4 border border-gray-300 rounded-lg cursor-pointer hover:bg-purple-50">
                <input
                  type="radio"
                  name="paymentPlan"
                  value="quarterly"
                  checked={formData.paymentPlan === 'quarterly'}
                  onChange={handleInputChange}
                  className="mr-3 text-purple-600"
                />
                <div>
                  <div className="font-medium text-gray-900">Quarterly</div>
                  <div className="text-sm text-gray-600">Save 5%</div>
                </div>
              </label>
              <label className="flex items-center p-4 border border-gray-300 rounded-lg cursor-pointer hover:bg-purple-50">
                <input
                  type="radio"
                  name="paymentPlan"
                  value="annual"
                  checked={formData.paymentPlan === 'annual'}
                  onChange={handleInputChange}
                  className="mr-3 text-purple-600"
                />
                <div>
                  <div className="font-medium text-gray-900">Annual</div>
                  <div className="text-sm text-gray-600">Save 10%</div>
                </div>
              </label>
            </div>
          </div>

          {/* Emergency Contact */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <i className="ri-phone-line text-purple-600 mr-2"></i>
              Emergency Contact
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="emergencyContact" className="block text-sm font-medium text-gray-700 mb-2">
                  Emergency Contact Name *
                </label>
                <input
                  type="text"
                  id="emergencyContact"
                  name="emergencyContact"
                  value={formData.emergencyContact}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                  placeholder="Emergency contact full name"
                />
              </div>
              <div>
                <label htmlFor="emergencyPhone" className="block text-sm font-medium text-gray-700 mb-2">
                  Emergency Contact Phone *
                </label>
                <input
                  type="tel"
                  id="emergencyPhone"
                  name="emergencyPhone"
                  value={formData.emergencyPhone}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>
          </div>

          {/* Medical Information */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <i className="ri-heart-pulse-line text-purple-600 mr-2"></i>
              Medical Information
            </h3>
            <div>
              <label htmlFor="medicalConditions" className="block text-sm font-medium text-gray-700 mb-2">
                Medical Conditions or Allergies
              </label>
              <textarea
                id="medicalConditions"
                name="medicalConditions"
                value={formData.medicalConditions}
                onChange={handleInputChange}
                rows={4}
                maxLength={500}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm resize-none"
                placeholder="Please list any medical conditions, allergies, or special requirements (max 500 characters)"
              />
              <div className="text-right text-sm text-gray-500 mt-1">
                {formData.medicalConditions.length}/500 characters
              </div>
            </div>
          </div>

          {/* Marketing Consent */}
          <div className="mb-8">
            <label className="flex items-start">
              <input
                type="checkbox"
                name="marketingConsent"
                checked={formData.marketingConsent}
                onChange={handleInputChange}
                className="mt-1 mr-3 text-purple-600 focus:ring-purple-500"
              />
              <span className="text-sm text-gray-700">
                I agree to receive marketing communications, program updates, and special offers from Elite Soccer Academy. 
                You can unsubscribe at any time.
              </span>
            </label>
          </div>

          {/* Payment Summary */}
          {formData.program && (
            <div className="mb-8 p-6 bg-purple-50 border border-purple-200 rounded-lg">
              <h3 className="text-lg font-semibold text-purple-900 mb-4">Payment Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-700">Registration Fee:</span>
                  <span className="font-medium">$80.00</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">Program Fee ({formData.paymentPlan}):</span>
                  <span className="font-medium">${(calculateTotalAmount() - 80).toFixed(2)}</span>
                </div>
                <div className="border-t border-purple-200 pt-2 mt-2">
                  <div className="flex justify-between text-lg font-semibold text-purple-900">
                    <span>Total Amount:</span>
                    <span>${calculateTotalAmount().toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Submit Status */}
          {submitStatus === 'error' && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <i className="ri-error-warning-line text-red-600 mr-2"></i>
                <span className="text-red-800">There was an error processing your registration. Please try again.</span>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="text-center">
            <button
              type="submit"
              disabled={isSubmitting || !formData.program}
              className="bg-purple-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {isSubmitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                  Processing...
                </>
              ) : (
                <>
                  <i className="ri-secure-payment-line mr-2"></i>
                  Pay ${formData.program ? calculateTotalAmount().toFixed(2) : '0.00'} & Complete Registration
                </>
              )}
            </button>
          </div>

          <div className="text-center mt-4 text-sm text-gray-600">
            <p>Secure payment powered by Stripe. Your payment information is encrypted and secure.</p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegistrationForm;
